import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Alert, Box, Button, FormControl, InputLabel, Link, MenuItem, Paper, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as searchInvoiceMetaData from '../../actions/invoiceDateActions';
import AppPagination, { DEFAULT_ROWS_PER_PAGE, pageSlice } from '../../components/common/AppPagination';
import { formatDateToYYYYMMDD } from '../../components/common/ReportParts';

const CRITERIA = [
    ['invoice', 'Invoice #'],
    ['vin', 'VIN'],
    ['plate', 'Plate'],
    ['or_dr', 'OR/DR #'],
];

const InvoiceSearchPage = ({ getInvoicesData, searchResults, resetData }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [criteria, setCriteria] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [searchError, setSearchError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        resetData();
    }, [resetData]);

    const handleSearch = (event) => {
        event.preventDefault();
        const hasDates = startDate && endDate;
        const hasSearchValue = criteria && searchValue.trim();
        if (!hasDates && !hasSearchValue) {
            setSearchError('Pick a date range, or choose what to search by and enter a value.');
            return;
        }
        if ((startDate && !endDate) || (!startDate && endDate)) {
            setSearchError('Pick both a start and end date.');
            return;
        }
        setSearchError('');
        setHasSearched(true);
        setPage(0);
        getInvoicesData({
            startDate: formatDateToYYYYMMDD(startDate),
            endDate: formatDateToYYYYMMDD(endDate),
            criteria,
            searchValue: searchValue.trim(),
        });
    };

    const openInvoice = (invoiceId) => window.open(`/invoice/${invoiceId}`, '_blank');

    return (
        <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleSearch}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        <DatePicker
                            label="From"
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate || undefined}
                            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                        />
                        <DatePicker
                            label="To"
                            value={endDate}
                            onChange={setEndDate}
                            minDate={startDate || undefined}
                            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                        />
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel id="search-by">Search by</InputLabel>
                            <Select
                                labelId="search-by"
                                label="Search by"
                                value={criteria}
                                onChange={(e) => {
                                    setCriteria(e.target.value);
                                    if (!e.target.value) setSearchValue('');
                                }}
                            >
                                <MenuItem value="">Dates only</MenuItem>
                                {CRITERIA.map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Value"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            disabled={!criteria}
                            sx={{ width: 220 }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Button type="submit" variant="contained">
                            Search
                        </Button>
                    </Box>
                </LocalizationProvider>
            </Paper>
            {searchError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {searchError}
                </Alert>
            )}

            <Paper variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>OR/DR #</TableCell>
                                <TableCell>Vehicle</TableCell>
                                <TableCell>Color</TableCell>
                                <TableCell>Plate</TableCell>
                                <TableCell>VIN</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageSlice(searchResults, page, rowsPerPage).map((invoice, index) => (
                                <TableRow
                                    hover
                                    key={invoice.invoiceId || index}
                                    onClick={() => openInvoice(invoice.invoiceId)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Link component="button" underline="hover" sx={{ fontWeight: 600 }}>
                                            {invoice.invoiceId}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{invoice.reason}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoice.invoiceCreatedDate}</TableCell>
                                    <TableCell>{invoice.vehicle_OR_DR_No}</TableCell>
                                    <TableCell>
                                        {[invoice.vehicle_Year, invoice.vehicle_Make, invoice.vehicle_Model].filter(Boolean).join(' ')}
                                    </TableCell>
                                    <TableCell>{invoice.vehicle_Color}</TableCell>
                                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{invoice.vehicle_Plate_No}</TableCell>
                                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{invoice.vehicle_VIN}</TableCell>
                                </TableRow>
                            ))}
                            {searchResults.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                                        <Typography color="text.secondary">
                                            {hasSearched
                                                ? 'No cleared invoices match this search.'
                                                : 'Search cleared invoices by date range, or by invoice #, VIN, plate or OR/DR #.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {searchResults.length > 0 && (
                    <AppPagination
                        count={searchResults.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                            setRowsPerPage(value);
                            setPage(0);
                        }}
                    />
                )}
            </Paper>
        </>
    );
};

const mapStateToProps = (state) => ({
    searchResults: (state.invoiceList && state.invoiceList.searchResults) || [],
    authInfo: state.authInfo,
});

const mapDispatchToProps = (dispatch) => ({
    getInvoicesData: (searchCriteria) => dispatch(searchInvoiceMetaData.searchInvoiceMetaData(searchCriteria)),
    resetData: () => dispatch(searchInvoiceMetaData.resetInvoiceData()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoiceSearchPage));
